import { WebActionEvents, WebPageEvents } from '@griply/types';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  CommandButton,
  LifeAreaRocket,
  Target,
} from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Icon } from 'shared/components/ui/icon';
import { KeyboardShortcut } from 'shared/components/ui/keyboard-shortcut';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { DesktopPlatform } from 'shared/utils/get-desktop-download';
import { getPlatformShortcut } from 'shared/utils/get-platform-shortcut';

import * as Styled from './desktop-download.style';

const SHORTCUT_TOKEN = '%SHORTCUT%';
const DESKTOP_APP_ICON_URL = '/images/onboarding/desktop-app-icon.png';

export type DesktopDownloadProps = {
  platform: DesktopPlatform;
  url: string;
  onDismiss: () => void;
};

export const DesktopDownload: React.FC<DesktopDownloadProps> = ({
  platform,
  url,
  onDismiss,
}) => {
  useTrackPageVisitEvent(WebPageEvents.OnboardingDesktopDownload, undefined);
  const { t } = useTranslation();
  const track = useTrackEvents();

  const isMac = platform === DesktopPlatform.Mac;
  const shortcutKeys = getPlatformShortcut(['Ctrl', 'Space']);
  const [quickEntryBefore, quickEntryAfter] = t(
    'dialogs.desktop-download.usps.quick-entry',
  )
    .split(SHORTCUT_TOKEN)
    .map((part) => part.trim());
  const downloadLabel = isMac
    ? t('dialogs.desktop-download.interactions.download.mac')
    : t('dialogs.desktop-download.interactions.download.windows');

  const onDownload = () => {
    track(WebActionEvents.OnboardingDesktopDownloadSubmit, undefined);
    window.location.assign(url);
    onDismiss();
  };

  const onSkip = () => {
    track(WebActionEvents.OnboardingDesktopSkipSubmit, undefined);
    onDismiss();
  };

  return (
    <Styled.Container>
      <Styled.Content>
        <Styled.AppIcon src={DESKTOP_APP_ICON_URL} alt="Griply" />

        <Typography variant="h4" component="h1" align="center">
          {t('dialogs.desktop-download.title')}
        </Typography>

        <Styled.UspList>
          <Styled.UspItem>
            <Styled.UspIcon>
              <Icon icon={CommandButton} />
            </Styled.UspIcon>
            <Styled.UspText>
              <span>{quickEntryBefore}</span>
              <KeyboardShortcut shortcut={shortcutKeys} />
              <span>{quickEntryAfter}</span>
            </Styled.UspText>
          </Styled.UspItem>

          <Styled.UspItem>
            <Styled.UspIcon>
              <Icon icon={LifeAreaRocket} />
            </Styled.UspIcon>
            <Styled.UspText>
              {t('dialogs.desktop-download.usps.performance')}
            </Styled.UspText>
          </Styled.UspItem>

          <Styled.UspItem>
            <Styled.UspIcon>
              <Icon icon={Target} />
            </Styled.UspIcon>
            <Styled.UspText>
              {t('dialogs.desktop-download.usps.goals')}
            </Styled.UspText>
          </Styled.UspItem>
        </Styled.UspList>

        <Styled.Buttons>
          <Button
            type="button"
            onClick={onDownload}
            startIcon={ArrowDown}
            size={Sizes.Large}
            fullWidth
            alignCenter
          >
            {downloadLabel}
          </Button>
          <Button
            type="button"
            onClick={onSkip}
            variant={Variants.Text}
            size={Sizes.Medium}
            color={Colors.Inherit}
            fullWidth
            alignCenter
          >
            {t('forms.onboarding.skip.label')}
          </Button>
        </Styled.Buttons>
      </Styled.Content>
    </Styled.Container>
  );
};
